/**
 * Conexiunea la Meta: tokenul și conturile de reclame.
 *
 * **Se salvează întâi, se testează după.** Același tipar ca la ExpertAccounts, unde
 * `test-connection` citește credențialele din baza de date și refuză dacă nu sunt scrise. Nu e o
 * alegere de comoditate: testul trebuie să probeze exact ce va folosi jobul, nu ce e în formular.
 *
 * **Conturile găsite nu se creează singure.** Un cont de reclame al altcuiva, partajat din
 * greșeală către Business Manager-ul nostru, n-are de ce să intre în CRM. Testul le listează, omul
 * bifează care intră.
 *
 * Specificația: `docs/superpowers/specs/2026-10-01-meta-ads-etapa1-design.md`.
 */
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'

interface Setari {
  appId: string | null
  apiVersion: string
  tokenSet: boolean
  enabled: boolean
  updatedAt: string | null
}

interface ContGasit {
  actId: string
  nume: string
  moneda: string
  stare: number | null
  existaInCrm: boolean
}

interface Cont {
  id: number
  actId: string
  nume: string
  moneda: string
  activ: boolean
  /** Seria facturilor Meta ale contului: `FBADS-314-…` → `314`. */
  serieFacturi: string | null
  ultimaSincronizareOk: string | null
  ultimaIncercare: string | null
  ultimaEroare: string | null
}

type Test =
  | { ok: true; numeUtilizator: string | null; conturi: ContGasit[] }
  | { ok: false; eroare: string; tokenInvalid: boolean }

const eroare = (e: unknown) => {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  toast.error(err?.response?.data?.error?.message ?? 'A eșuat.')
}

export function SetariMetaPage() {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.meta:write')

  const [token, setToken] = useState('')
  const [appId, setAppId] = useState('')
  const [apiVersion, setApiVersion] = useState('')
  const [rezultat, setRezultat] = useState<Test | null>(null)

  const setari = useQuery<{ settings: Setari }>({
    queryKey: ['meta-settings'],
    queryFn: async () => (await api.get('/meta/settings')).data,
  })
  const conturi = useQuery<{ conturi: Cont[] }>({
    queryKey: ['meta-ad-accounts'],
    queryFn: async () => (await api.get('/meta/ad-accounts')).data,
  })

  const s = setari.data?.settings

  /*
    App ID-ul salvat sta IN camp, nu ca text gri de fundal. Ca fundal nu se putea sterge: campul
    era deja gol, iar un camp gol nu se trimitea — pe 06.10.2026 ramasese salvata adresa de email
    pusa acolo de completarea automata a browserului, desi fusese „stearsa".
  */
  useEffect(() => {
    if (setari.data) setAppId(setari.data.settings.appId ?? '')
  }, [setari.data])
  const appIdSchimbat = appId.trim() !== (s?.appId ?? '')

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.put('/meta/settings', {
          ...(token.trim() ? { token: token.trim() } : {}),
          ...(appIdSchimbat ? { appId: appId.trim() || null } : {}),
          ...(apiVersion.trim() ? { apiVersion: apiVersion.trim() } : {}),
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meta-settings'] })
      setToken('')
      toast.success('Salvat. Acum testează conexiunea.')
    },
    onError: eroare,
  })

  const testeaza = useMutation({
    mutationFn: async () => (await api.post<Test>('/meta/settings/test-connection', {})).data,
    onSuccess: (r) => {
      setRezultat(r)
      if (r.ok) toast.success(`Conectat ca ${r.numeUtilizator ?? '(fără nume)'}.`)
      else toast.error(r.eroare, { duration: 15_000 })
    },
    onError: eroare,
  })

  const adauga = useMutation({
    mutationFn: async (c: ContGasit) =>
      (await api.post('/meta/ad-accounts', { actId: c.actId, nume: c.nume, moneda: c.moneda }))
        .data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meta-ad-accounts'] })
      /* Testul se reface, ca „deja în CRM" să fie adevărat imediat, nu după o reîncărcare. */
      testeaza.mutate()
      toast.success('Cont adăugat.')
    },
    onError: eroare,
  })

  const scoate = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/meta/ad-accounts/${id}`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meta-ad-accounts'] })
      toast.success('Contul nu se mai urmărește. Cheltuiala citită rămâne.')
    },
    onError: eroare,
  })

  /*
    Seria facturilor leaga facturile din contabilitate de cont, pentru „Facturi Meta" de pe
    ecranul Meta Ads. Meta nu da facturile prin API la plata cu cardul.
  */
  const serie = useMutation({
    mutationFn: async (v: { id: number; serieFacturi: string | null }) =>
      (await api.patch(`/meta/ad-accounts/${v.id}`, { serieFacturi: v.serieFacturi })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meta-ad-accounts'] })
      toast.success('Seria facturilor salvată.')
    },
    onError: eroare,
  })

  const porneste = useMutation({
    mutationFn: async (enabled: boolean) => (await api.put('/meta/settings', { enabled })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['meta-settings'] }),
    onError: eroare,
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări Meta</h1>

      <Card description="Tokenul e al unui System User din Business Manager și nu expiră. Se ține criptat; ecranul nu-l arată niciodată înapoi.">
        <div className="space-y-3">
          <Field
            label="Token System User"
            type="password"
            // „new-password": altfel browserul ia formularul drept unul de autentificare si pune
            // adresa de email in campul de dinainte — App ID.
            autoComplete="new-password"
            placeholder={s?.tokenSet ? '••••••••  (pus — scrie altul ca să-l înlocuiești)' : ''}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={!poateScrie}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="App ID (opțional)"
              autoComplete="off"
              inputMode="numeric"
              placeholder="un număr, ex. 1234567890123456"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              disabled={!poateScrie}
            />
            <Field
              label="Versiune Graph API"
              autoComplete="off"
              placeholder={s?.apiVersion ?? 'v21.0'}
              value={apiVersion}
              onChange={(e) => setApiVersion(e.target.value)}
              disabled={!poateScrie}
            />
          </div>

          {poateScrie && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                loading={salveaza.isPending}
                disabled={!token.trim() && !appIdSchimbat && !apiVersion.trim()}
                onClick={() => salveaza.mutate()}
              >
                Salvează
              </Button>
              {/*
                Testarea cere tokenul SALVAT, nu pe cel din formular: trebuie probat exact ce va
                folosi jobul. De-aia butonul e stins cât timp nu e nimic salvat.
              */}
              <Button
                variant="secondary"
                disabled={!s?.tokenSet}
                loading={testeaza.isPending}
                onClick={() => testeaza.mutate()}
              >
                Testează conexiunea
              </Button>
              {!s?.tokenSet && (
                <span className="text-xs text-b4l-text-muted">Salvează întâi tokenul.</span>
              )}
              <label className="ml-auto flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={s?.enabled ?? false}
                  onChange={(e) => porneste.mutate(e.target.checked)}
                />
                Citește cheltuiala zilnic
              </label>
            </div>
          )}
        </div>
      </Card>

      {rezultat && !rezultat.ok && (
        <Card>
          <p className="text-sm text-b4l-red">{rezultat.eroare}</p>
          {rezultat.tokenInvalid && (
            <p className="mt-1 text-xs text-b4l-text-muted">
              Tokenul nu mai e valid: a fost revocat, System User-ul a fost șters, sau i s-a scos
              rolul de pe contul de reclame. Generează altul în Business Settings → System Users.
            </p>
          )}
        </Card>
      )}

      {rezultat?.ok && (
        <Card
          description={`Conectat ca ${rezultat.numeUtilizator ?? '(fără nume)'}. Conturile de mai jos sunt cele pe care le vede tokenul — bifează care intră în CRM.`}
        >
          {rezultat.conturi.length === 0 ? (
            <p className="text-sm text-b4l-text-muted">
              Tokenul nu vede niciun cont de reclame. În Business Settings → System Users →{' '}
              <strong>Add Assets</strong>, dă-i contul cu permisiunea <em>View Performance</em>.
            </p>
          ) : (
            <ul className="divide-y divide-b4l-border">
              {rezultat.conturi.map((c) => (
                <li key={c.actId} className="flex items-center gap-3 py-2 text-sm">
                  <span className="flex-1">
                    {c.nume}
                    <span className="ml-2 font-mono text-xs text-b4l-text-muted">{c.actId}</span>
                  </span>
                  <span className="text-xs text-b4l-text-muted">{c.moneda}</span>
                  {c.existaInCrm ? (
                    <span className="text-xs text-b4l-green-dark">deja în CRM</span>
                  ) : (
                    poateScrie && (
                      <Button
                        className="!px-2 !py-1 !text-xs"
                        loading={adauga.isPending}
                        onClick={() => adauga.mutate(c)}
                      >
                        Adaugă
                      </Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card description="Conturile urmărite. Scoaterea din urmărire nu șterge cheltuiala deja citită.">
        {(conturi.data?.conturi.length ?? 0) === 0 ? (
          <p className="text-sm text-b4l-text-muted">
            Niciun cont. Salvează tokenul, testează conexiunea și adaugă-le de acolo.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Cont</th>
                <th className="py-2 pr-3 font-medium">Monedă</th>
                <th
                  className="py-2 pr-3 font-medium"
                  title="Numărul din mijloc al facturilor Meta ale contului: FBADS-314-… → 314"
                >
                  Seria facturilor
                </th>
                <th className="py-2 pr-3 font-medium">Ultima sincronizare</th>
                {poateScrie && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {(conturi.data?.conturi ?? []).map((c) => (
                <tr
                  key={c.id}
                  className={`border-b border-b4l-border last:border-0 ${c.activ ? '' : 'opacity-50'}`}
                >
                  <td className="py-2 pr-3">
                    {c.nume}
                    <span className="ml-2 font-mono text-xs text-b4l-text-muted">{c.actId}</span>
                    {!c.activ && <span className="ml-2 text-xs">(scos din urmărire)</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs">{c.moneda}</td>
                  <td className="py-2 pr-3 text-xs">
                    {poateScrie ? (
                      <input
                        className="w-20 rounded border border-b4l-border px-2 py-1 font-mono text-xs"
                        defaultValue={c.serieFacturi ?? ''}
                        placeholder="ex. 314"
                        inputMode="numeric"
                        onBlur={(e) => {
                          const v = e.target.value.trim()
                          if (v !== (c.serieFacturi ?? '')) {
                            serie.mutate({ id: c.id, serieFacturi: v || null })
                          }
                        }}
                      />
                    ) : (
                      (c.serieFacturi ?? '—')
                    )}
                  </td>
                  {/*
                    Eroarea se arată lângă ultima reușită, nu în locul ei: altfel un eșec de o zi
                    ar face să pară că n-a mers niciodată.
                  */}
                  <td className="py-2 pr-3 text-xs">
                    {c.ultimaSincronizareOk
                      ? new Date(c.ultimaSincronizareOk).toLocaleString('ro-RO')
                      : '—'}
                    {c.ultimaEroare && (
                      <span className="block text-b4l-red" title={c.ultimaEroare}>
                        ultima încercare a eșuat
                      </span>
                    )}
                  </td>
                  {poateScrie && (
                    <td className="py-2 pr-3">
                      {c.activ && (
                        <Button
                          variant="secondary"
                          className="!px-2 !py-1 !text-xs"
                          onClick={() => scoate.mutate(c.id)}
                        >
                          Scoate din urmărire
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card description="Ce ai de făcut o singură dată, în business.facebook.com, ca tokenul să existe.">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-b4l-text">
          <li>
            <strong>Business Settings → Users → System Users → Add</strong> — nume
            <em> CRM Boost4Life</em>, rol <em>Employee</em>.
          </li>
          <li>
            Pe System User → <strong>Add Assets → Ad Accounts</strong> → bifezi contul → permisiunea{' '}
            <em>View Performance</em>.
          </li>
          <li>
            <strong>Generate New Token</strong> → aplicația Boost4Life → bifezi{' '}
            <code>ads_read</code> → <strong>Generate Token</strong>.
          </li>
          <li>
            Tokenul se arată <strong>o singură dată</strong>. Îl lipești mai sus, salvezi, apoi
            testezi.
          </li>
        </ol>
      </Card>
    </div>
  )
}
